/** Which image sources an ad body may render, shared by the page and editor.
 *
 * Unlike video there is no host allowlist. Video needs one because an embed is
 * an iframe that runs the provider's JavaScript, and three providers cover
 * essentially every real link. Images are different on both counts: an <img>
 * cannot execute anything, and the source is normally the client's own site —
 * an allowlist would block the common case while adding little over requiring
 * https. What an external image does leak is the visitor's IP and which page
 * they are on, so it is loaded without a referrer.
 */

import { isUploadedMediaSrc } from "./uploaded-media";
import { withHttpsScheme } from "./url-scheme";

export const IMAGE_SOURCE_HINT =
	"an https:// address ending in .png, .jpg, .webp, .gif or .avif";

/** A line that is nothing but an image URL, so pasting one just works — the
 *  same rule video follows. */
const IMAGE_LINE = /^https:\/\/\S+\.(?:png|jpe?g|webp|gif|avif)(?:\?\S*)?$/i;

/** The same, for an ad's own upload on a dev checkout, where the address the
 *  upload endpoint returns is root-relative and so has no scheme to match. */
const UPLOADED_IMAGE_LINE = /^\/\S+\.(?:png|jpe?g|webp|gif|avif)(?:\?\S*)?$/i;

export function matchImageLine(line: string): string | null {
	if (IMAGE_LINE.test(line)) return line;
	return UPLOADED_IMAGE_LINE.test(line) && isUploadedMediaSrc(line) ? line : null;
}

/** The source as it should be stored, filling in https for a bare host. */
export function normaliseImageSrc(input: string): string | null {
	if (isUploadedMediaSrc(input)) return input;
	const src = withHttpsScheme(input);
	return src !== null && IMAGE_LINE.test(src) ? src : null;
}

export function isAllowedImageSrc(src: string): boolean {
	// Our own uploads are also allowed unschemed: a dev checkout has no jobs
	// domain, so the endpoint returns a path, and a path is same-origin by
	// construction.
	return src.startsWith("https://") || isUploadedMediaSrc(src);
}

/** The <img> markup, or null when the source is not one we will load. */
export function imageEmbedHtml(alt: string, src: string): string | null {
	if (!isAllowedImageSrc(src)) return null;
	return `<img src="${src}" alt="${alt}" loading="lazy" referrerpolicy="no-referrer">`;
}
